import React from 'react';

import cx from 'classnames';
import * as R from 'ramda';

import { FontAwesomeIcon } from 'components/shared/Icon/FontAwesome';
import { EOrganizedBy } from 'detailsv2/structure';
import { getEventPlankTitle } from 'utils/itineraryDetails';

import { Period } from '../../../types';
import * as constants from './constants';
import s from './index.module.css';
import * as types from './types';
import { checkForErrors } from './utils';

type Props = {
  event: types.CalendarEvent;
  calendarDay: Pick<types.TCalendarDay, 'dayNumber'>;
  period: Period;
  expandableChild?: false | React.ReactNode;
  wrapperProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
  plankProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
  isSelected?: boolean;
  isTemp?: boolean;
  organizedBy?: EOrganizedBy;
  planksStyles?: React.CSSProperties;
  isPlaceholder?: boolean;
};

export const EventPlankLayout = (props: Props) => {
  const isContinuationAhead = React.useMemo(() => {
    const endPeriodDay = R.last(props.period);
    const eventEndDayNumber = props.event.daysRange.end;

    if (!endPeriodDay) return false;
    if (!eventEndDayNumber) return false;

    if (eventEndDayNumber > endPeriodDay?.dayNumber) return true;
  }, [props.event.daysRange.end, props.period]);

  const isContinuationBehind = React.useMemo(() => {
    const startPeriodDay = R.head(props.period);
    const eventStartDayNumber = props.event.daysRange.start;

    if (!startPeriodDay) return false;

    return startPeriodDay.dayNumber > eventStartDayNumber;
  }, [props.event.daysRange.start, props.period]);

  const eventType = React.useMemo(() => props.event.type, [props.event.type]);
  const eventTitle = React.useMemo(() => getEventPlankTitle(props.event), [props.event]);

  const eventColor = React.useMemo(() => {
    return constants.COLOR_MAP[eventType];
  }, [eventType]);

  return (
    <div className={s.plank} {...props.wrapperProps}>
      {props.expandableChild}

      <div
        className={cx(s.plank, s['plank-tail'], {
          [s['plank-tail-active']]: props.event.temp,
          [s['plank-behind']]: isContinuationBehind,
          [s['plank-ahead']]: isContinuationAhead,
          [s['plank-selected']]: props.isSelected,
          [s['plank-temp']]: props.isTemp,
          [s['plank-error']]:
            checkForErrors(props.event) ||
            //@ts-ignore
            (props.calendarDay.date !== props.event.datesRange.start &&
              !isContinuationBehind &&
              props.organizedBy === EOrganizedBy.Date &&
              process.env.REACT_APP_ENV !== 'production'),
        })}
        style={{
          borderColor: 'transparent',
          backgroundColor: eventColor,
          ...props.planksStyles,
        }}
        {...props.plankProps}
      >
        <div className={s['plank-content']}>
          <div className={cx(s['icon-wrapper'], { [s['icon-wrapper-hidden']]: isContinuationBehind })}>
            <FontAwesomeIcon className={props.event.icon} />
          </div>
        </div>

        {!isContinuationBehind && (
          <span className={cx(s['text-truncate'])} dangerouslySetInnerHTML={{ __html: eventTitle }} />
        )}
      </div>
    </div>
  );
};
